import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useNavigate } from 'react-router-dom';
import { User, AlertCircle, Trash2, LogOut, Edit2, X, Check } from 'lucide-react';
import { updateProfile } from 'firebase/auth';

const PersonalInfo = () => {
  const { settings } = useSettings(); // Keep settings if needed elsewhere, otherwise remove
  const { isDarkMode } = useDarkMode();
  const { currentUser, deleteCurrentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(currentUser?.displayName || '');
  const [updateError, setUpdateError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if user is an Apple anonymous user
  const isAppleAnonymous = currentUser?.providerData?.[0]?.providerId === 'apple.com' && 
                          currentUser?.email?.includes('privaterelay.appleid.com');

  const sectionStyles = {
    container: `space-y-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    header: `flex items-center gap-3 mb-6`,
    headerIcon: isDarkMode ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-emerald-600",
    headerText: `text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    card: `p-6 rounded-[24px] border transition-all duration-200 ${isDarkMode ? 'bg-[#111111] border-gray-800 shadow-lg shadow-black/5' : 'bg-white border-gray-100 shadow-sm'}`,
    label: `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`,
    value: `text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    button: {
      primary: `px-4 py-2.5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${isDarkMode ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`,
      danger: `px-4 py-2.5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${isDarkMode ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30' : 'bg-red-600 text-white hover:bg-red-700'}`,
      dangerConfirm: `px-4 py-2.5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${isDarkMode ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-red-700 text-white hover:bg-red-800'}`
    },
    alert: `p-4 rounded-xl border flex items-start gap-3 ${isDarkMode ? 'bg-red-900/30 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`,
    alertIcon: `w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`,
    input: `w-full px-4 py-2 rounded-xl border transition-all duration-200 ${isDarkMode ? 'bg-[#1A1A1A] border-gray-800 text-white focus:border-emerald-500' : 'bg-white border-gray-200 text-gray-900 focus:border-emerald-600'} focus:ring-0 focus:outline-none`,
    editButton: `p-2 rounded-lg transition-all duration-200 ${isDarkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'}`
  };

  const handleDeleteRequest = () => {
    setConfirmDelete(true);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCurrentUser();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setIsDeleting(false);
    setDeleteError(null);
  };

  const handleUpdateName = async () => {
    if (!newDisplayName.trim()) {
      setUpdateError('Name cannot be empty');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      await updateProfile(currentUser, { displayName: newDisplayName.trim() });
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating display name:', error);
      setUpdateError(error.message || 'Failed to update name. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    // Add scrollable container with iOS-friendly scroll behavior and increased bottom padding
    <div className="flex flex-col h-full overflow-y-auto pb-32" style={{WebkitOverflowScrolling: 'touch'}}>
      <div className="space-y-12 w-full">
        {/* Personal Information Display Section */} 
        <section className={sectionStyles.container}>
          <div className={sectionStyles.header}>
            <User className={sectionStyles.headerIcon} />
            <h3 className={sectionStyles.headerText}>Your Information</h3>
          </div>

          <div className={sectionStyles.card}>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={sectionStyles.label}>Nickname</span>
                  {!isEditingName && !currentUser?.displayName && (
                    <span className={`text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      Add a name to personalize your experience
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4">
                  {isEditingName ? (
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newDisplayName}
                          onChange={(e) => setNewDisplayName(e.target.value)}
                          className={sectionStyles.input}
                          placeholder="Enter your name"
                          disabled={isUpdating}
                        />
                        <button
                          onClick={handleUpdateName}
                          disabled={isUpdating}
                          className={`${sectionStyles.editButton} text-emerald-500 hover:text-emerald-400`}
                          title="Save name"
                        >
                          <Check size={20} />
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingName(false);
                            setNewDisplayName(currentUser?.displayName || '');
                            setUpdateError(null);
                          }}
                          disabled={isUpdating}
                          className={sectionStyles.editButton}
                          title="Cancel"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      {updateError && (
                        <p className="mt-2 text-sm text-red-500">{updateError}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className={sectionStyles.value}>
                        {currentUser?.displayName || 'Not set'}
                      </p>
                      <button
                        onClick={() => setIsEditingName(true)}
                        className={sectionStyles.editButton}
                        title="Edit name"
                      >
                        <Edit2 size={20} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div>
                <span className={sectionStyles.label}>Email Address</span>
                <p className={sectionStyles.value}>
                  {isAppleAnonymous ? 'Private Apple Email' : currentUser?.email || 'No email found'}
                </p>
              </div>
              {/* Add other fields if needed, e.g., Provider */} 
              {currentUser?.providerData?.[0]?.providerId && (
                   <div>
                     <span className={sectionStyles.label}>Sign-in method</span>
                     <p className={sectionStyles.value}> 
                       {currentUser.providerData[0].providerId === 'google.com' ? 'Google' :
                        currentUser.providerData[0].providerId === 'apple.com' ? 'Apple' :
                        currentUser.providerData[0].providerId === 'password' ? 'Email/Password' :
                        currentUser.providerData[0].providerId}
                     </p>
                   </div>
              )}
            </div>
          </div>
        </section>

        {/* Delete Account Section */} 
        <section className={sectionStyles.container}>
          <div className={sectionStyles.header}>
            <Trash2 className={`${sectionStyles.headerIcon} ${isDarkMode ? '!text-red-400' : '!text-red-600'}`} />
            <h3 className={`${sectionStyles.headerText} ${isDarkMode ? '!text-red-400' : '!text-red-600'}`}>Delete Account</h3>
          </div>

          <div className={sectionStyles.card}>
            <div className="space-y-6">
              <div className={sectionStyles.alert}>
                <AlertCircle className={sectionStyles.alertIcon} />
                <div>
                  <h4 className="font-medium mb-1">Warning: This action is permanent</h4>
                  <p className="text-sm">
                    Deleting your account will permanently remove all your data, including settings, progress, goals, and personal information. This data cannot be recovered.
                  </p>
                </div>
              </div>

              {deleteError && (
                <div className={sectionStyles.alert}>
                  <AlertCircle className={sectionStyles.alertIcon} />
                  <p className="text-sm">{deleteError}</p>
                </div>
              )}

              {!confirmDelete ? (
                <button 
                  onClick={handleDeleteRequest}
                  className={sectionStyles.button.danger}
                  disabled={isDeleting}
                >
                  <Trash2 size={18} />
                  Delete My Account
                </button>
              ) : (
                <div className="space-y-4 p-4 rounded-lg border ${isDarkMode ? 'border-red-500/30 bg-red-900/20' : 'border-red-200 bg-red-50'}">
                  <p className={`text-center font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                    Are you absolutely sure you want to delete your account?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={handleConfirmDelete}
                      className={`${sectionStyles.button.dangerConfirm} w-full sm:w-auto`}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete Permanently'}
                    </button>
                    <button
                      onClick={handleCancelDelete}
                      className={`${sectionStyles.button.primary} ${isDarkMode ? '!bg-gray-600/20 !text-gray-300 hover:!bg-gray-600/30' : '!bg-gray-200 !text-gray-700 hover:!bg-gray-300'} w-full sm:w-auto`}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
               {/* Re-authentication hint if needed */} 
               {deleteError && deleteError.includes('sign back in') && (
                  <div className={`mt-4 flex items-center gap-2 text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      <LogOut size={16} />
                      <span>For security, please sign out and sign back in before trying again.</span>
                  </div>
               )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PersonalInfo;